import { memo, type CSSProperties, type ReactNode } from 'react';

/**
 * Plaque — a carved ceremonial plate that HUGS its text.
 *
 * CSS-ONLY (no image texture). An earlier version backed the plate with the
 * `plaque-clean`/`plaque-cracked` webp as a cover-cropped texture, but that art
 * carries its OWN baked ornate frame + wood-plank composition, so any crop behind
 * a variable-width plate showed arbitrary zoomed plank smears and fragments of the
 * baked frame bleeding mid-plate. The art can't back a variable-width plate, so it
 * is gone. The plate is now built entirely from the token ramps — the same family
 * as `LowerThird`'s caption box, but full-framed:
 *
 *  - flat dark ink/wood-tone gradient fill (no image, subtle top→bottom ramp);
 *  - a 2px gold (clean) / blood (cracked) border from the token ramps;
 *  - a faint inner bevel: a hairline highlight at the top edge + soft inner shadow;
 *  - a soft drop shadow to lift the plate off the scene;
 *  - the box sizes to its CONTENT (`inline-flex` + `fit-content`), with a
 *    `minInlineSize` floor so a 3-letter name still reads as a substantial plate,
 *    and an optional `maxInlineSize` cap so a long line wraps instead of becoming
 *    a frame-spanning banner.
 *
 * `variant`:
 *   'clean'   — warm ink + gold rules (room code, "no one fell", the seat plaque)
 *   'cracked' — blood-tinted ink + crimson rules (the dawn victim plate)
 *
 * Type is the caller's job — pass the name/code as `children` styled in Grenze.
 * `overlay` renders above the fill but below the text (e.g. the claw-gouge).
 */

export type PlaqueVariant = 'clean' | 'cracked';

export interface PlaqueProps {
  children: ReactNode;
  variant?: PlaqueVariant;
  /** Inner padding (block/inline). Defaults scale for the broadcast design space. */
  padBlock?: number | string;
  padInline?: number | string;
  /** Floor on the plate width so short names don't collapse to a chip. */
  minInlineSize?: number | string;
  /** Cap on the plate width — a long line wraps rather than spanning the frame. */
  maxInlineSize?: number | string;
  /** Corner radius of the carved frame. */
  radius?: number | string;
  /** Extra style hooks (positioning, etc.) merged onto the outer element. */
  style?: CSSProperties;
  className?: string;
  /** Rendered above the fill, below the text (claw-gouge slot). */
  overlay?: ReactNode;
}

const FRAME: Record<
  PlaqueVariant,
  { fill: string; rule: string; bevelHi: string; shadow: string }
> = {
  clean: {
    // Warm ink → wood-tone gradient, flat and subtle (no image).
    fill: 'linear-gradient(180deg, #1a1109 0%, var(--ink-1, #0b0709) 100%)',
    rule: 'var(--gold-4, #c9923f)',
    // Faint gold highlight for the top-edge bevel.
    bevelHi: 'rgba(230,184,102,0.28)',
    shadow:
      '0 18px 44px rgba(0,0,0,0.62), 0 2px 10px rgba(0,0,0,0.5)',
  },
  cracked: {
    // Blood-tinted ink → deeper ink gradient.
    fill: 'linear-gradient(180deg, #24080c 0%, var(--ink-0, #060406) 100%)',
    rule: 'var(--blood-3, #802636)',
    bevelHi: 'rgba(194,47,62,0.30)',
    shadow:
      '0 18px 44px rgba(0,0,0,0.66), 0 2px 10px rgba(0,0,0,0.5)',
  },
};

function PlaqueImpl({
  children,
  variant = 'clean',
  padBlock = 28,
  padInline = 56,
  minInlineSize = 220,
  maxInlineSize,
  radius = 18,
  style,
  className,
  overlay,
}: PlaqueProps) {
  const f = FRAME[variant];
  return (
    <div
      className={className}
      style={{
        position: 'relative',
        display: 'inline-flex',
        inlineSize: 'fit-content',
        maxInlineSize: maxInlineSize ?? '100%',
        minInlineSize,
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        paddingBlock: padBlock,
        paddingInline: padInline,
        borderRadius: radius,
        background: f.fill,
        // 2px carved rule from the token ramp.
        border: `2px solid ${f.rule}`,
        // Bevel + lift: hairline highlight at the top inner edge, a soft inner
        // ambient-occlusion shadow, and a soft outer drop shadow.
        boxShadow: `${f.shadow}, inset 0 1px 0 ${f.bevelHi}, inset 0 -18px 30px rgba(0,0,0,0.45)`,
        isolation: 'isolate',
        ...style,
      }}
    >
      {overlay}
      <div
        style={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 8,
          minWidth: 0,
        }}
      >
        {children}
      </div>
    </div>
  );
}

export const Plaque = memo(PlaqueImpl);
